import React, { useMemo, useState } from 'react';
import { Circle, CheckCircle2, RotateCcw } from 'lucide-react';
import { Section, Card } from '../shared/primitives';
import HospitalIcon from '../../shared/icons/HospitalIcon';

/*
 * Cardiac Surgery & Transplant Postoperative Handoff (BSLMC/BCM badge buddy,
 * v1.3, May 2023) as a working checklist. The four universal sections always
 * show; Transplant and MCS appear when switched on; only the receiving unit
 * that applies (ICU or CV PACU) is shown. Individual lines can also carry a
 * condition (`when`), so e.g. the anesthesiologist's list differs for an
 * extubated PACU patient. Lines tick off as they're covered.
 */

const ICU = (f) => f.destination === 'icu';
const PACU = (f) => f.destination === 'pacu';

const SECTIONS = [
  { id: 'pre', title: 'Pre-handoff', items: [
    'Confirm patient ID and allergies',
    { text: 'Complete initial tasks', note: 'Connect to the monitor, send labs, and the other usual arrival tasks before the handoff begins' },
    'Form sterile huddle',
    'Team introductions',
  ] },
  { id: 'rn', title: 'RN', items: ['Confirm patient ID', 'Skin integrity'] },
  { id: 'surgeon', title: 'Surgeon', items: [
    'Pertinent PMH', 'Surgery performed and indication', 'Surgical course / intraop issues',
    'Times: CPB, AoXC, circ arrest, etc.', 'Chest tubes, drains, pacing wires', 'Concerns', 'Surgical pathway orders complete?',
  ] },
  // In the order the anesthetic happened: preop, airway, antibiotics, course,
  // block/analgesia, fluids and labs, rhythm, current state at handoff, concerns.
  { id: 'anesthesia', title: 'Anesthesiologist', items: [
    'Preop status: neuro, O₂, meds, MCS',
    'Airway / mask ventilation and lines',
    'Antibiotics',
    'Anesthetic course / intraop issues',
    'Intraoperative TEE (pre + post)',
    { text: 'Nerve block?', when: ICU },
    { text: 'Nerve block: type, local anesthetic dose, time', when: PACU },
    { text: 'Analgesics given: total opioid and non-opioid doses', when: PACU },
    { text: 'PONV prophylaxis given', when: PACU },
    'I/O: fluids, blood products, UOP, EBL',
    'Pertinent lab results',
    'Rhythm + pacemaker/AICD settings',
    'Current drips (+ concentration)',
    { text: 'Last paralytic: dose + timing', when: ICU },
    { text: 'NMB reversal: agent and dose', when: PACU },
    { text: 'Extubation: ease, any difficulty or trauma, O₂ requirement', when: PACU },
    'Concerns',
  ] },
  { id: 'mcs', title: 'MCS', when: (f) => f.mcs, items: ['MCS type', 'Cannulation site(s)', 'MCS settings: flow, sweep, DO₂'] },
  { id: 'transplant', title: 'Transplant', when: (f) => f.transplant, items: [
    'Relevant donor characteristics', 'Organ ischemic time, OCS used?', 'Induction immunosuppression',
    'Lung: postop bronch performed?', 'Lung: ventilator settings', 'Pulmonary vasodilators?', 'Early graft function',
  ] },
  { id: 'icu', title: 'ICU', when: (f) => f.destination === 'icu', items: [
    'Fast track eligibility', 'Anticipated postop concerns', 'Sedation / analgesia', 'Hemodynamic goals',
    'Anticoagulation / antiplatelet plan', 'Renal replacement therapy?', 'Any questions?',
  ] },
  { id: 'pacu', title: 'CV PACU', when: (f) => f.destination === 'pacu', items: [
    'Postop orders: labs, imaging?', 'Anesthesia PACU orders completed?', 'Postop destination: floor vs ICU', 'Any questions?',
  ] },
];

const Toggle = ({ on, onClick, children }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={() => { onClick(); }}
    className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
      on ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    }`}
  >
    {children}
  </button>
);

const PostopHandoffAid = () => {
  const [flags, setFlags] = useState({ transplant: false, mcs: false, destination: 'icu' });
  const [checked, setChecked] = useState(() => new Set());

  // Sections that apply, each with only the lines that apply
  const visible = useMemo(
    () => SECTIONS
      .filter((s) => !s.when || s.when(flags))
      .map((s) => ({
        ...s,
        items: s.items
          .filter((it) => typeof it === 'string' || !it.when || it.when(flags))
          .map((it) => (typeof it === 'string' ? { text: it } : it)),
      })),
    [flags]
  );
  const toggleItem = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const reset = () => { setChecked(new Set()); };

  return (
    <div className="space-y-6">
      {/* The badge buddy's own header: this is an official BSLMC / BCM tool */}
      <div className="bg-[#005587] text-white rounded-2xl p-5 shadow-card text-center">
        <div className="flex items-center justify-center gap-5 mb-3" style={{ filter: 'brightness(0) invert(1)' }}>
          <HospitalIcon type="st-lukes" className="w-9 h-9" />
          <HospitalIcon type="texas-heart" className="w-9 h-9" />
        </div>
        <p className="text-[11px] font-semibold tracking-[0.14em] text-white/80 uppercase">Baylor St. Luke's Medical Center</p>
        <h2 className="text-lg font-bold leading-tight mt-1">Cardiac Surgery &amp; Transplant<br />Postoperative Handoff</h2>
        <p className="text-xs text-white/70 mt-2">Version 1.3 · May 2023</p>
      </div>

      {/* This patient */}
      <Section
        title="This patient"
        aside={checked.size > 0 && (
          <button onClick={reset} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
            <RotateCcw size={13} /> Reset
          </button>
        )}
      >
        <Card>
          <div className="px-4 py-3 flex items-center gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 w-24 flex-shrink-0">Going to</span>
            <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-xl p-1 grid grid-cols-2 gap-1" role="tablist">
              {[{ id: 'icu', label: 'ICU' }, { id: 'pacu', label: 'CV PACU' }].map((d) => (
                <button
                  key={d.id}
                  role="tab"
                  aria-selected={flags.destination === d.id}
                  onClick={() => { setFlags((f) => ({ ...f, destination: d.id })); }}
                  className={`py-1.5 rounded-lg text-sm font-semibold transition-all ${
                    flags.destination === d.id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="px-4 py-3 flex items-center gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 w-24 flex-shrink-0">Also cover</span>
            <div className="flex gap-2">
              <Toggle on={flags.transplant} onClick={() => setFlags((f) => ({ ...f, transplant: !f.transplant }))}>Transplant</Toggle>
              <Toggle on={flags.mcs} onClick={() => setFlags((f) => ({ ...f, mcs: !f.mcs }))}>MCS</Toggle>
            </div>
          </div>
        </Card>
      </Section>

      {visible.map((section) => {
        const done = section.items.filter((_, i) => checked.has(`${section.id}:${i}`)).length;
        return (
          <Section
            key={section.id}
            title={section.title}
            aside={<span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{done}/{section.items.length}</span>}
          >
            <Card>
              {section.items.map((item, i) => {
                const key = `${section.id}:${i}`;
                const isDone = checked.has(key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleItem(key)}
                    aria-pressed={isDone}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left"
                  >
                    {isDone
                      ? <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
                      : <Circle size={20} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />}
                    <span className="min-w-0">
                      <span className={`block text-[15px] leading-snug ${isDone ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                        {item.text}
                      </span>
                      {item.note && (
                        <span className={`block text-xs mt-0.5 leading-snug ${isDone ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                          {item.note}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </Card>
          </Section>
        );
      })}
    </div>
  );
};

export default PostopHandoffAid;
